/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext } from 'react';
import { bool, object } from 'prop-types';
import LazyLoad from 'react-lazy-load';
import { QueryClient, QueryClientProvider } from 'react-query';

import exportWithBoundary from '../../../helpers/exportWithBoundary';
import useTranslations from '../../../hooks/useTranslations';
import { queryDefaults } from '../../_data_sources/constants';
import JobMap from './JobMap';
import JobMapProvider, { JobMapContext } from './JobMapContext';
import JobDetail from './JobMapJobDetail';
import JobList from './JobMapJobList';
import JobLocation from './JobMapJobLocation';
import { arrowLeft, arrowRight } from './icons';
import {
  IconWrap,
  JobMapContainer,
  Logo,
  Map,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHandle,
} from './styles';

const queryClient = new QueryClient(queryDefaults);
const t = useTranslations('JobMapShow');

function JobMapShow({ jobMap, hasRemoteJobs }) {
  window.I18n.locale = jobMap.language;
  return (
    <QueryClientProvider client={queryClient}>
      <JobMapProvider hasRemoteJobs={hasRemoteJobs} jobMap={jobMap}>
        <JobMapView />
      </JobMapProvider>
    </QueryClientProvider>
  );
}

function JobMapView() {
  const {
    currentJob,
    currentPlace,
    currentView,
    effectiveBrand,
    jobMapId,
    jobMapName,
    logo,
    setShowSidebar,
    showSidebar,
  } = useContext(JobMapContext);

  return (
    <LazyLoad>
      <JobMapContainer showSidebar={showSidebar}>
        <Sidebar>
          <SidebarContent currentView={currentView}>
            <JobList />
            {currentPlace ? <JobLocation /> : <div />}
            {currentJob ? <JobDetail /> : <div />}
            <SidebarFooter>
              <a
                href={`https://www.careerarc.com/social-recruiting/job-maps?utm_medium=web&utm_source=job_map&utm_campaign=job_map:${jobMapId}&utm_content=powered_by_footer`}
                target='_blank'
                rel='noreferrer'
              >
                {t('job_maps.powered_by')}
              </a>
              <a
                href={window.Routes.terms_path()}
                target='_blank'
                rel='noreferrer'
              >
                {t('job_maps.terms')}
              </a>
              <a
                href={window.Routes.privacy_path()}
                target='_blank'
                rel='noreferrer'
              >
                {t('job_maps.privacy')}
              </a>
            </SidebarFooter>
          </SidebarContent>
          <SidebarHandle
            onClick={() => {
              setShowSidebar((prev) => !prev);
            }}
          >
            <IconWrap size={28} color='neutral-10'>
              {showSidebar ? arrowLeft : arrowRight}
            </IconWrap>
          </SidebarHandle>
        </Sidebar>
        <Map>
          <JobMap />
        </Map>
        {logo && (
          <Logo src={logo} alt={`${effectiveBrand?.name} - ${jobMapName}`} />
        )}
      </JobMapContainer>
    </LazyLoad>
  );
}

JobMapShow.propTypes = {
  jobMap: object.isRequired,
  hasRemoteJobs: bool,
};

JobMapShow.defaultProps = {
  hasRemoteJobs: false,
};

function JobMapShowFallback() {
  return <JobMapContainer />;
}

export default exportWithBoundary(JobMapShow, JobMapShowFallback);
